import cx from "classnames";
import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import CodeChanges from "components/CodeChanges";
import { CodeChangesGroupNode } from "components/CodeChanges/types";
import CollapsiblePanel from "components/CollapsiblePanel";
import CollapsiblePanelContent from "components/CollapsiblePanel/Content";
import CollapsiblePanelHeader from "components/CollapsiblePanel/Header";
import CollapsiblePanelTitle from "components/CollapsiblePanel/Title";
import CollapsiblePanelToggleTrigger from "components/CollapsiblePanel/ToggleTrigger";
import { ArrowRight } from "components/icons/generated";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import TextEllipsis from "ds/components/TextEllipsis";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import { EntityChangeType } from "types/generated";

import ChangeTypeIcon from "../../ChangeTypeIcon";
import { getChangeType } from "../../helpers";
import { normalizePath, populateChangeTree } from "../../ResourcesDiff/helpers";
import EntityChangeResultBadge from "../EntityChangeResultBadge";
import { EntityChangeWithId } from "../types";
import { useChangesStore } from "../useChangesStore";
import ReplacementIndicator from "./ReplacementIndicator";
import styles from "./styles.module.css";

type RunChangesListItemNewProps = {
  item: EntityChangeWithId;
  onCheckItem: (item: EntityChangeWithId, checked: boolean) => unknown;
  checked: boolean;
  selectable: boolean;
};

const RunChangesListItemNew = ({
  item,
  onCheckItem,
  checked,
  selectable,
}: RunChangesListItemNewProps) => {
  const collapsed = useChangesStore(
    useShallow(({ getChange }) => {
      return getChange(item.id)?.collapsed ?? false;
    })
  );
  const toggleChange = useChangesStore(
    useShallow(({ toggleChange }) => {
      return () => toggleChange(item.id);
    })
  );
  const collapsedGroups = useChangesStore(
    useShallow(({ getChange }) => {
      return getChange(item.id)?.collapsedGroups ?? new Set<string>();
    })
  );
  const updateChangeGroups = useChangesStore(
    useShallow(({ updateChangeGroups }) => {
      return (collapsedGroups: Set<string>) => updateChangeGroups(item.id, collapsedGroups);
    })
  );

  const handleCheckItem = useCallback(() => {
    onCheckItem(item, !checked);
  }, [onCheckItem, item, checked]);

  const { moved, result } = item.metadata;

  const changeType = getChangeType(item.metadata);

  const parsedItemsNewCodeChanges = useMemo((): CodeChangesGroupNode => {
    const root: CodeChangesGroupNode = {
      id: "root",
      children: [],
    };

    if (collapsed) return root;

    item.attributeChanges.forEach((change) => {
      const keys = normalizePath(change.path);

      populateChangeTree(change, keys, root);
    });

    return root;
  }, [item, collapsed]);

  const hasNoChanges = parsedItemsNewCodeChanges.children.length === 0;

  return (
    <CollapsiblePanel isCollapsed={collapsed} onToggle={toggleChange}>
      <CollapsiblePanelHeader align="center" justify="between" padding="medium large" gap="medium">
        <Box align="center" gap="medium">
          <CollapsiblePanelToggleTrigger
            ariaLevel={3}
            ariaLabel={
              moved
                ? `${item.previousAddress}
                  moves to
                  ${item.address}`
                : item.address
            }
          />

          {selectable && (
            <Toggle
              variant="checkbox"
              id={`change-${item.id}`}
              onChange={handleCheckItem}
              isSelected={checked}
              dataTestId="run-changes-list-item-checkbox"
              aria-label={`Select ${item.address} for replanning`}
              value={item.address}
            />
          )}

          <ChangeTypeIcon type={item.metadata.type} moved={item.metadata.moved} />

          <TextEllipsis tooltip={item.address} tooltipWidthMode="maxWidthSm">
            {({ className, ...props }) => (
              <CollapsiblePanelTitle {...props} className={cx(className, styles.panelTitle)}>
                {moved ? (
                  <>
                    {item.previousAddress}
                    <Icon src={ArrowRight} color="secondary" />
                    {item.address}
                  </>
                ) : (
                  item.address
                )}
              </CollapsiblePanelTitle>
            )}
          </TextEllipsis>

          {(changeType === EntityChangeType.ReplaceCreateBeforeDestroy ||
            changeType === EntityChangeType.ReplaceDestroyBeforeCreate) && (
            <ReplacementIndicator changeType={changeType} />
          )}
        </Box>
        <EntityChangeResultBadge type={result} />
      </CollapsiblePanelHeader>
      {!collapsed && (
        <CollapsiblePanelContent padding="0 large large">
          <Box fullWidth relative className={styles.details}>
            {hasNoChanges && item.metadata.moved ? (
              <Box fullWidth>
                <Typography tag="span">Resource moved without any modifications.</Typography>
              </Box>
            ) : (
              <CodeChanges
                changes={parsedItemsNewCodeChanges}
                collapsedGroups={collapsedGroups}
                onGroupToggle={updateChangeGroups}
              />
            )}
          </Box>
        </CollapsiblePanelContent>
      )}
    </CollapsiblePanel>
  );
};

export default RunChangesListItemNew;
